import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Eye, Play, RefreshCw, Landmark } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { wiseReferenceBackfillAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

// Same chain/job-poll shape as InvoiceTaxonomyBackfillPanel.jsx, adapted for
// this backfill's fields (updated/no_payer_reference/unresolvable instead
// of applied/skipped/unresolvable_direction) and the fact every candidate
// costs up to 2 live Wise API calls, not a local re-parse.
const POLL_INTERVAL_MS = 2000;
const HEARTBEAT_STALE_SECONDS = 60;

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

function fmtErrorKey(key) {
  if (key === 'retryable' || key === 'network_error') return 'network/other';
  return `HTTP ${key}`;
}

function FetchErrorBreakdown({ live }) {
  const counts = live?.fetch_error_counts;
  if (!counts || Object.keys(counts).length === 0) return null;
  return (
    <div className="text-xs space-y-1" data-testid="wise-ref-backfill-fetch-error-breakdown">
      <p className="text-muted-foreground">Fetch error breakdown:</p>
      <div className="flex flex-wrap gap-3">
        {Object.entries(counts).map(([key, count]) => (
          <span key={key} className="text-amber-600">
            {fmtErrorKey(key)}: <strong>{fmtInt(count)}</strong>
          </span>
        ))}
      </div>
      {live.first_fetch_error_message && (
        <p className="text-muted-foreground font-mono truncate">
          First error: {live.first_fetch_error_message}
        </p>
      )}
    </div>
  );
}

function CohortBreakdown({ breakdown }) {
  if (!breakdown) return null;
  return (
    <div className="text-xs text-muted-foreground" data-testid="wise-ref-backfill-cohort-breakdown">
      Cohort by id shape -- real transfers: <strong>{fmtInt(breakdown.real_transfer)}</strong>,
      {' '}wise_act_ (unresolvable): <strong>{fmtInt(breakdown.wise_act_synthetic)}</strong>,
      {' '}fee legs: <strong>{fmtInt(breakdown.fee_leg)}</strong>,
      {' '}interbalance legs: <strong>{fmtInt(breakdown.interbalance_leg)}</strong>
      {breakdown.other_unresolvable > 0 && <>, other unresolvable: <strong>{fmtInt(breakdown.other_unresolvable)}</strong></>}
    </div>
  );
}

function secondsSince(isoString) {
  if (!isoString) return null;
  const then = new Date(isoString).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 1000));
}

function useJobPolling({ onCompleted, onFailed, onProgress }) {
  const [polling, setPolling] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);
  const startTimeRef = useRef(null);

  const stop = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (elapsedRef.current) { clearInterval(elapsedRef.current); elapsedRef.current = null; }
    startTimeRef.current = null;
    setPolling(false);
  }, []);

  useEffect(() => stop, [stop]);

  const start = useCallback((jobId) => {
    stop();
    setPolling(true);
    setElapsed(0);
    startTimeRef.current = Date.now();

    elapsedRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    pollRef.current = setInterval(async () => {
      try {
        const { data } = await wiseReferenceBackfillAPI.jobStatus(jobId);
        if (data.status === 'completed') {
          stop();
          onCompleted(data.result);
        } else if (data.status === 'failed') {
          stop();
          onFailed(data.error, data.progress || null);
        } else if (data.status === 'running') {
          onProgress(data.progress || null);
        }
      } catch (err) {
        stop();
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stop, onCompleted, onFailed, onProgress]);

  return { polling, elapsed, start };
}

const SAMPLE_DISPLAY_LIMIT = 10;

function SampleCorrections({ operation, samples }) {
  if (!samples || samples.length === 0) return null;
  const shown = samples.slice(0, SAMPLE_DISPLAY_LIMIT);
  return (
    <div className="text-xs space-y-1 pt-1" data-testid={`wise-ref-backfill-${operation}-samples`}>
      <p className="text-muted-foreground">
        Sample corrections (first {shown.length} of {samples.length}) -- confirm these look like real
        payer text before running Apply:
      </p>
      <div className="border rounded overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left font-medium px-2 py-1">Transaction</th>
              <th className="text-left font-medium px-2 py-1">Before</th>
              <th className="text-left font-medium px-2 py-1">After</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((s) => (
              <tr key={s.id} className="border-b last:border-b-0">
                <td className="px-2 py-1 font-mono">{s.id}</td>
                <td className="px-2 py-1 text-muted-foreground">{s.old_reference ?? '-'}</td>
                <td className="px-2 py-1">{s.new_reference ?? <span className="text-amber-600">no reference found</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ChainSection({
  operation, title, icon, chain, progress, failed, polling, elapsed, starting, stopping,
  onRun, onStop, runLabel, runDisabled, showSamples,
}) {
  const running = chain?.status === 'running';
  const live = progress || chain?.seed;
  const cohortCount = chain?.metadata?.cohort_count;
  const processed = live?.cohort_processed;
  const heartbeatAge = secondsSince(chain?.heartbeat_at);
  const stalled = running && !polling && heartbeatAge != null && heartbeatAge > HEARTBEAT_STALE_SECONDS;
  const errorText = (chain?.status === 'error' && chain?.error) || failed;

  return (
    <div className="border rounded-lg p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium flex items-center gap-1">{icon}{title}</span>
        <Button data-testid={`wise-ref-backfill-${operation}-run`} onClick={onRun} disabled={runDisabled} size="sm">
          {(starting || polling) ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
          {running
            ? `Chain running, run ${fmtInt(chain.runs_count)}, ${fmtInt(processed)}/${fmtInt(cohortCount)}`
            : runLabel}
        </Button>
        {running && (
          <Button data-testid={`wise-ref-backfill-${operation}-stop`} onClick={onStop} disabled={stopping} size="sm" variant="outline">
            {stopping ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Stop Chain
          </Button>
        )}
        {polling && (
          <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
            <RefreshCw className="h-3 w-3 animate-spin" />
            {elapsed}s
          </span>
        )}
      </div>

      {chain && (
        <div className="text-xs space-y-1" data-testid={`wise-ref-backfill-${operation}-chain-summary`}>
          <div className="flex items-center gap-1">
            <Badge variant={running ? 'default' : chain.status === 'error' ? 'destructive' : 'outline'}>
              {chain.status}
            </Badge>
            <span className="text-muted-foreground">
              run {fmtInt(chain.runs_count)}
              {cohortCount != null && <> -- {fmtInt(processed)} of {fmtInt(cohortCount)} processed</>}
            </span>
            {stalled && (
              <span className="text-amber-600 dark:text-amber-400">
                last active {heartbeatAge}s ago -- watchdog will resume it
              </span>
            )}
          </div>

          {live && (
            <div className="flex flex-wrap gap-3">
              <span>Updated: <strong>{fmtInt(live.updated)}</strong></span>
              {live.stamped_current > 0 && (
                <span className="text-muted-foreground">
                  Stamped only (no field change): <strong>{fmtInt(live.stamped_current)}</strong>
                </span>
              )}
              {live.already_current > 0 && (
                <span className="text-red-600">
                  Already current (unexpected no-op): <strong>{fmtInt(live.already_current)}</strong>
                </span>
              )}
              {live.no_payer_reference > 0 && (
                <span className="text-amber-600">
                  No payer reference found: <strong>{fmtInt(live.no_payer_reference)}</strong>
                </span>
              )}
              {live.fetch_failed > 0 && (
                <span className="text-amber-600">
                  Live fetch failed (will retry): <strong>{fmtInt(live.fetch_failed)}</strong>
                </span>
              )}
              {live.fetch_not_found > 0 && (
                <span className="text-amber-600">
                  Transfer not found (stopped retrying): <strong>{fmtInt(live.fetch_not_found)}</strong>
                </span>
              )}
              {live.unresolvable > 0 && (
                <span className="text-amber-600">
                  Unresolvable (no profile/resource id): <strong>{fmtInt(live.unresolvable)}</strong>
                </span>
              )}
              {live.kept_foreign_reference > 0 && (
                <span className="text-muted-foreground">
                  Kept existing reference: <strong>{fmtInt(live.kept_foreign_reference)}</strong>
                </span>
              )}
            </div>
          )}

          <FetchErrorBreakdown live={live} />
          <CohortBreakdown breakdown={chain?.metadata?.cohort_breakdown} />

          {errorText && <p className="text-red-600 dark:text-red-400">{errorText}</p>}

          {showSamples && <SampleCorrections operation={operation} samples={live?.samples} />}
        </div>
      )}
    </div>
  );
}

export function WiseReferenceBackfillPanel() {
  const [previewChain, setPreviewChain] = useState(null);
  const [previewProgress, setPreviewProgress] = useState(null);
  const [previewFailed, setPreviewFailed] = useState(null);
  const [previewStarting, setPreviewStarting] = useState(false);
  const [previewStopping, setPreviewStopping] = useState(false);

  const [applyChain, setApplyChain] = useState(null);
  const [applyProgress, setApplyProgress] = useState(null);
  const [applyFailed, setApplyFailed] = useState(null);
  const [applyStarting, setApplyStarting] = useState(false);
  const [applyStopping, setApplyStopping] = useState(false);

  const fetchPreviewChain = useCallback(async () => {
    try {
      const { data } = await wiseReferenceBackfillAPI.previewChainStatus();
      setPreviewChain(data?.chain || null);
    } catch {
      // Non-fatal: the chain summary just won't render/update this tick.
    }
  }, []);

  const fetchApplyChain = useCallback(async () => {
    try {
      const { data } = await wiseReferenceBackfillAPI.applyChainStatus();
      setApplyChain(data?.chain || null);
    } catch {
      // Non-fatal.
    }
  }, []);

  const previewPoll = useJobPolling({
    onCompleted: useCallback((result) => {
      setPreviewProgress(null);
      setPreviewFailed(null);
      fetchPreviewChain();
      toast.success(result?.truncated ? 'Preview run complete -- continuing chain' : 'Preview complete');
    }, [fetchPreviewChain]),
    onFailed: useCallback((error, lastProgress) => {
      setPreviewProgress((prev) => lastProgress ?? prev);
      setPreviewFailed(error);
      fetchPreviewChain();
      toast.error(error ?? 'Preview run failed');
    }, [fetchPreviewChain]),
    onProgress: useCallback((progress) => setPreviewProgress(progress), []),
  });

  const applyPoll = useJobPolling({
    onCompleted: useCallback((result) => {
      setApplyProgress(null);
      setApplyFailed(null);
      fetchApplyChain();
      toast.success(result?.truncated ? 'Apply run complete -- continuing chain' : 'Apply complete');
    }, [fetchApplyChain]),
    onFailed: useCallback((error, lastProgress) => {
      setApplyProgress((prev) => lastProgress ?? prev);
      setApplyFailed(error);
      fetchApplyChain();
      toast.error(error ?? 'Apply run failed');
    }, [fetchApplyChain]),
    onProgress: useCallback((progress) => setApplyProgress(progress), []),
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await wiseReferenceBackfillAPI.jobLatest('preview');
        const job = data?.job;
        if (job?.status === 'running') {
          setPreviewProgress(job.progress || null);
          previewPoll.start(job._id);
        } else if (job?.status === 'failed') {
          setPreviewProgress(job.progress || null);
          setPreviewFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await wiseReferenceBackfillAPI.jobLatest('apply');
        const job = data?.job;
        if (job?.status === 'running') {
          setApplyProgress(job.progress || null);
          applyPoll.start(job._id);
        } else if (job?.status === 'failed') {
          setApplyProgress(job.progress || null);
          setApplyFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchPreviewChain(); }, [fetchPreviewChain]);
  useEffect(() => { fetchApplyChain(); }, [fetchApplyChain]);

  useEffect(() => {
    if (previewChain?.status !== 'running') return undefined;
    const id = setInterval(fetchPreviewChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [previewChain?.status, fetchPreviewChain]);

  useEffect(() => {
    if (applyChain?.status !== 'running') return undefined;
    const id = setInterval(fetchApplyChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [applyChain?.status, fetchApplyChain]);

  const runPreview = async () => {
    setPreviewStarting(true);
    setPreviewFailed(null);
    try {
      const { data } = await wiseReferenceBackfillAPI.previewChainStart();
      if (data.job_id) {
        previewPoll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('A preview chain is already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchPreviewChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewStarting(false);
    }
  };

  const runApply = async () => {
    const cohortCount = previewChain?.metadata?.cohort_count ?? applyChain?.metadata?.cohort_count;
    if (!window.confirm(
      `Re-derive the payer reference for ${cohortCount ?? 'the'} Wise transaction(s) synced before the mapping fix? ` +
      'Each one is a live call to Wise -- this may take a while for a large backlog.'
    )) return;
    setApplyStarting(true);
    setApplyFailed(null);
    try {
      const { data } = await wiseReferenceBackfillAPI.applyChainStart();
      if (data.job_id) {
        applyPoll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('An apply chain is already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchApplyChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplyStarting(false);
    }
  };

  const stopPreviewChain = async () => {
    if (!previewChain?._id) return;
    if (!window.confirm('Stop the running Preview chain? The current run finishes its time window normally.')) return;
    setPreviewStopping(true);
    try {
      await wiseReferenceBackfillAPI.previewChainStop(previewChain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchPreviewChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewStopping(false);
    }
  };

  const stopApplyChain = async () => {
    if (!applyChain?._id) return;
    if (!window.confirm('Stop the running Apply chain? The current run finishes its time window normally.')) return;
    setApplyStopping(true);
    try {
      await wiseReferenceBackfillAPI.applyChainStop(applyChain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchApplyChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplyStopping(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Landmark className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Wise Reference Backfill</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Re-derives the payer-entered reference for Wise transactions synced before the
          reference-mapping fix (they carry Wise's own opaque transfer id as `reference`
          instead). No raw payload is stored for Wise, so every candidate is a live
          re-fetch from the Wise API -- both Preview and Apply are budget-boxed chains
          that self-schedule successor runs until the backlog converges.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <ChainSection
          operation="preview"
          title="Preview"
          icon={<Eye className="h-3 w-3" />}
          chain={previewChain}
          progress={previewProgress}
          failed={previewFailed}
          polling={previewPoll.polling}
          elapsed={previewPoll.elapsed}
          starting={previewStarting}
          stopping={previewStopping}
          onRun={runPreview}
          onStop={stopPreviewChain}
          runLabel="Preview"
          runDisabled={previewStarting || previewPoll.polling || previewChain?.status === 'running'}
          showSamples
        />
        <ChainSection
          operation="apply"
          title="Apply"
          icon={<Play className="h-3 w-3" />}
          chain={applyChain}
          progress={applyProgress}
          failed={applyFailed}
          polling={applyPoll.polling}
          elapsed={applyPoll.elapsed}
          starting={applyStarting}
          stopping={applyStopping}
          onRun={runApply}
          onStop={stopApplyChain}
          runLabel={previewChain?.metadata?.cohort_count != null ? `Apply (${fmtInt(previewChain.metadata.cohort_count)} candidates)` : 'Apply'}
          runDisabled={applyStarting || applyPoll.polling || applyChain?.status === 'running'}
        />
      </CardContent>
    </Card>
  );
}
